import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { LuDownload } from 'react-icons/lu';

import { Month } from '@ndvr/billing-api';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthApi, BillingApi, CustomerApi, PortfolioApi } from 'API';
import { isFutureDate, titleCase, todayDate } from 'utils';

// Roles Report block
const useRoleReport = () =>
    useQuery({
        queryKey: ['roleReport'],
        queryFn: async () => {
            return await AuthApi().personRoleReport();
        },
        refetchOnWindowFocus: false,
        enabled: false,
    });

// Household Report block
const useHouseholdReport = () =>
    useQuery({
        queryKey: ['householdReport'],
        queryFn: async () => {
            return await CustomerApi().householdReport();
        },
        refetchOnWindowFocus: false,
        enabled: false,
    });

// Account Report block
const useAccountCustodyReport = () =>
    useQuery({
        queryKey: ['accountCustodyReport'],
        queryFn: async () => {
            return await PortfolioApi().generateAccountCustodyReport();
        },
        refetchOnWindowFocus: false,
        enabled: false,
    });

// Billing Report block
const useGenerateIBFeeReport = () =>
    useMutation({
        mutationFn: (request: { reportYear: number; reportMonth: Month }) => {
            return BillingApi().generateIBFeeReport(request);
        },
    });

const useGenerateSchwabFeeReport = () =>
    useMutation({
        mutationFn: (request: { reportYear: number; reportMonth: Month }) => {
            return BillingApi().generateSchwabFeeReport(request);
        },
    });

const useGenerateFidelityFeeReport = () =>
    useMutation({
        mutationFn: (request: { reportYear: number; reportMonth: Month }) => {
            return BillingApi().generateFidelityFeeReport(request);
        },
    });

const useGenerateBillingValidationReport = () =>
    useMutation({
        mutationFn: (request: { reportYear: number; reportMonth: Month }) => {
            return BillingApi().generateValidationReport(request);
        },
    });

const useGetMonthsWithCompletedFeeGeneration = (billingYear?: number) =>
    useQuery({
        queryKey: ['billingReport', billingYear, 'months'],
        queryFn: async () => {
            const [response] = await BillingApi().getMonthsWithCompletedFeeGeneration(billingYear!);
            return response ?? [];
        },
        enabled: !!billingYear,
    });

export const ReportsAdmin: React.FC = () => {
    // roles
    const { isFetching: isFetchingRoles, refetch: downloadRolesReport } = useRoleReport();

    // households (global only)
    const { isFetching: isFetchingHousehold, refetch: downloadHouseholdReport } = useHouseholdReport();

    // accounts
    const { isFetching: isFetchingAccountCustodyReport, refetch: downloadAccountCustodyReport } =
        useAccountCustodyReport();

    // billing
    const currentYear = todayDate().getFullYear();
    const AVAILABLE_YEARS = [...Array(currentYear - 2022 + 1).keys()].map((i) => i + 2022);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const { data: completedMonths, isLoading: isLoadingMonths } = useGetMonthsWithCompletedFeeGeneration(selectedYear);

    return (
        <div className="flex flex-col gap-6">
            {/* Household Reports */}
            <Card>
                <CardHeader className="pt-6 pb-0">
                    <CardTitle className="font-heading font-medium">Household Report</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-row items-center gap-3">
                        <Button onClick={() => downloadHouseholdReport()} disabled={isFetchingHousehold}>
                            {isFetchingHousehold && (
                                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            )}
                            <LuDownload className="mr-2 h-4 w-4" /> Household Report
                        </Button>
                    </div>
                    <p className="text-muted-foreground mt-3 text-sm italic">
                        Generating the report might take several seconds
                    </p>
                </CardContent>
            </Card>

            {/* Account Report */}
            <Card>
                <CardHeader className="pt-6 pb-0">
                    <CardTitle className="font-heading font-medium">Account Report</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-row items-center gap-3">
                        <Button
                            onClick={() => downloadAccountCustodyReport()}
                            disabled={isFetchingAccountCustodyReport}
                        >
                            {isFetchingAccountCustodyReport && (
                                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            )}
                            <LuDownload className="mr-2 h-4 w-4" /> Account Custody Report
                        </Button>
                    </div>
                    <p className="text-muted-foreground mt-3 text-sm italic">
                        Generating the report might take several seconds
                    </p>
                </CardContent>
            </Card>

            {/* Billing Reports */}
            <Card>
                <CardHeader className="pt-6 pb-0">
                    <CardTitle className="font-heading font-medium">Billing Reports</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4 flex items-center gap-4">
                        <span className="text-sm">Report Year</span>
                        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                            <SelectTrigger className="w-[120px]">
                                <SelectValue placeholder="Year" />
                            </SelectTrigger>
                            <SelectContent>
                                {AVAILABLE_YEARS.map((year) => (
                                    <SelectItem key={year} value={String(year)}>
                                        {year}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {isLoadingMonths ? (
                        <div className="flex h-[208px] items-center justify-center">
                            <span className="mr-2 inline-block h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        </div>
                    ) : (
                        <div className="grid auto-rows-[50px] grid-cols-[150px_1fr]">
                            <div className="flex items-center">
                                <span className="text-muted-foreground text-xs uppercase">Month</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-muted-foreground text-xs uppercase">Actions</span>
                            </div>
                            {Object.values(Month)
                                .filter((_, i) => !isFutureDate(new Date(selectedYear, i, 1)))
                                .map((month) => (
                                    <React.Fragment key={month}>
                                        <div className="flex items-center border-b py-2 last:border-b-0">
                                            <span className="text-sm">{titleCase(month)}</span>
                                        </div>
                                        <div className="flex items-center border-b py-2 last:border-b-0">
                                            <ReportActions
                                                year={selectedYear}
                                                month={month}
                                                isFeeGenerationComplete={!!completedMonths?.includes(Month[month])}
                                            />
                                        </div>
                                    </React.Fragment>
                                ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Roles Report */}
            <Card>
                <CardHeader className="pt-6 pb-0">
                    <CardTitle className="font-heading font-medium">Roles Report</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-row items-center gap-3">
                        <Button onClick={() => downloadRolesReport()} disabled={isFetchingRoles}>
                            {isFetchingRoles && (
                                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            )}
                            <LuDownload className="mr-2 h-4 w-4" /> Person Roles Report
                        </Button>
                    </div>
                    <p className="text-muted-foreground mt-3 text-sm italic">
                        Generating the report might take several seconds
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

const ReportActions: React.FC<{ year: number; month: Month; isFeeGenerationComplete: boolean }> = ({
    year,
    month,
    isFeeGenerationComplete,
}) => {
    const generateIBFeesReport = useGenerateIBFeeReport();
    const generateSchwabFeesReport = useGenerateSchwabFeeReport();
    const generateFidelityFeesReport = useGenerateFidelityFeeReport();
    const generateBillingValidationReport = useGenerateBillingValidationReport();

    if (!isFeeGenerationComplete) {
        return <span className="text-muted-foreground text-sm italic">Reports are not available</span>;
    }

    return (
        <div className="flex items-center justify-center gap-3">
            <Button
                variant="secondary"
                size="sm"
                onClick={() => generateIBFeesReport.mutate({ reportYear: year, reportMonth: month })}
                disabled={generateIBFeesReport.isPending}
            >
                {generateIBFeesReport.isPending && (
                    <span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                <LuDownload className="mr-2 h-4 w-4" /> IB Fees
            </Button>
            <Button
                variant="secondary"
                size="sm"
                onClick={() => generateSchwabFeesReport.mutate({ reportYear: year, reportMonth: month })}
                disabled={generateSchwabFeesReport.isPending}
            >
                {generateSchwabFeesReport.isPending && (
                    <span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                <LuDownload className="mr-2 h-4 w-4" /> Schwab Fees
            </Button>
            <Button
                variant="secondary"
                size="sm"
                onClick={() => generateFidelityFeesReport.mutate({ reportYear: year, reportMonth: month })}
                disabled={generateFidelityFeesReport.isPending}
            >
                {generateFidelityFeesReport.isPending && (
                    <span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                <LuDownload className="mr-2 h-4 w-4" /> Fidelity Fees
            </Button>
            <Button
                variant="secondary"
                size="sm"
                onClick={() => generateBillingValidationReport.mutate({ reportYear: year, reportMonth: month })}
                disabled={generateBillingValidationReport.isPending}
            >
                {generateBillingValidationReport.isPending && (
                    <span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                )}
                <LuDownload className="mr-2 h-4 w-4" /> Billing Validation
            </Button>
        </div>
    );
};
